import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useMutation } from "convex/react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "convex/_generated/api";
import { useTranslation } from "@/packages/utils";
import { motion, AnimatePresence } from "framer-motion";
import {
  AlertTriangle,
  CheckCircle2,
  XCircle,
  ChevronsUpDown,
  Check,
  X,
  Loader2,
  ShieldAlert,
  Search,
} from "lucide-react";
import { Button, Input } from "@/components/ui/primitives";
import { Avatar } from "@/components/ui";
import type { AdminActionConfig } from "./admin-action-registry";

type ExecutionState = "idle" | "loading" | "success" | "error";

function UserAvatar({
  name,
  imageUrl,
  size = "md",
}: {
  name: string;
  imageUrl?: string;
  size?: "sm" | "md";
}) {
  return (
    <Avatar
      name={name || "?"}
      avatarUrl={imageUrl}
      avatarColor="#f59e0b"
      size={size === "sm" ? "xs" : "sm"}
      className={size === "sm" ? "h-7! w-7!" : "h-9! w-9!"}
    />
  );
}

function UserSelectField({
  fieldName,
  placeholder,
  value,
  onChange,
}: {
  fieldName: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation(["admin"]);
  const { data: users, isLoading } = useQuery(
    convexQuery(api.admin_actions.listAllUsers, {}),
  );

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (open && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [open]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setOpen(false);
      setSearch("");
    }
  }, []);

  const filteredUsers = useMemo(() => {
    if (!users) return [];
    const lowerSearch = search.toLowerCase();
    if (!lowerSearch) return users;
    return users.filter(
      (u) =>
        u.name.toLowerCase().includes(lowerSearch) ||
        u.email.toLowerCase().includes(lowerSearch),
    );
  }, [users, search]);

  const selectedUser = useMemo(() => {
    if (!value || !users) return null;
    return users.find((u) => u._id === value) ?? null;
  }, [value, users]);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        {t("actions.loadingUsers")}
      </div>
    );
  }

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: dropdown container needs keyboard navigation
    <div ref={containerRef} className="relative" onKeyDown={handleKeyDown}>
      {/* Trigger button */}
      <button
        type="button"
        id={fieldName}
        onClick={() => setOpen((prev) => !prev)}
        className={`
          flex h-12 w-full items-center gap-3 rounded-lg
          border bg-background px-3 py-2 text-sm
          ring-offset-background transition-all duration-200
          focus:outline-none focus:ring-2 focus:ring-amber-400/40 focus:ring-offset-2
          hover:border-amber-300/60 hover:shadow-sm
          ${open ? "border-amber-400/60 ring-2 ring-amber-400/20 shadow-sm" : "border-input"}
        `}
      >
        {selectedUser ? (
          <div className="flex items-center gap-2.5 min-w-0 flex-1">
            <UserAvatar
              name={selectedUser.name}
              imageUrl={selectedUser.imageUrl}
              size="sm"
            />
            <div className="flex flex-col items-start min-w-0">
              <span
                className="font-semibold text-foreground text-sm truncate max-w-full"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              >
                {selectedUser.name || "\u2014"}
              </span>
              <span className="text-[11px] text-muted-foreground truncate max-w-full">
                {selectedUser.email || "\u2014"}
              </span>
            </div>
          </div>
        ) : (
          <span className="text-muted-foreground flex-1 text-left">
            {placeholder}
          </span>
        )}
        <motion.div
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground/50" />
        </motion.div>
      </button>

      {/* Dropdown panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.96 }}
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
            className="absolute z-50 mt-2 w-full rounded-lg border border-amber-200/40 bg-white shadow-xl shadow-black/8 overflow-hidden"
          >
            {/* Search header */}
            <div className="p-2 border-b border-border/50 bg-muted/30">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground/50" />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder={placeholder}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="
                    h-9 w-full rounded-md border border-input bg-background pl-8 pr-3 text-sm
                    placeholder:text-muted-foreground/50
                    focus:outline-none focus:ring-2 focus:ring-amber-400/30 focus:border-amber-300/60
                    transition-colors
                  "
                  style={{ fontFamily: "'JetBrains Mono', monospace" }}
                />
              </div>
            </div>

            {/* User list */}
            <div className="max-h-64 overflow-y-auto py-1">
              {filteredUsers.length === 0 ? (
                <div className="py-8 text-center">
                  <p className="text-sm text-muted-foreground">
                    {t("actions.noUsersFound")}
                  </p>
                </div>
              ) : (
                filteredUsers.map((user, index) => {
                  const isSelected = value === user._id;
                  return (
                    <motion.button
                      key={user._id}
                      type="button"
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.15, delay: index * 0.02 }}
                      className={`
                        flex w-full items-center gap-3 px-2.5 py-2 mx-1 rounded-md text-sm
                        transition-colors duration-150 cursor-pointer
                        ${
                          isSelected
                            ? "bg-amber-50 ring-1 ring-amber-200/60"
                            : "hover:bg-amber-50/50"
                        }
                      `}
                      style={{ width: "calc(100% - 0.5rem)" }}
                      onClick={() => {
                        onChange(user._id);
                        setOpen(false);
                        setSearch("");
                      }}
                    >
                      <UserAvatar
                        name={user.name}
                        imageUrl={user.imageUrl}
                        size="sm"
                      />
                      <div className="flex flex-col items-start min-w-0 flex-1">
                        <span
                          className="font-semibold text-foreground truncate max-w-full text-[13px]"
                          style={{ fontFamily: "'JetBrains Mono', monospace" }}
                        >
                          {user.name || "\u2014"}
                        </span>
                        <span className="text-[11px] text-muted-foreground truncate max-w-full">
                          {user.email || "\u2014"}
                        </span>
                      </div>
                      <div
                        className={`shrink-0 transition-opacity duration-150 ${isSelected ? "opacity-100" : "opacity-0"}`}
                      >
                        <div className="flex h-5 w-5 items-center justify-center rounded-full bg-amber-500 text-white">
                          <Check className="h-3 w-3" strokeWidth={3} />
                        </div>
                      </div>
                    </motion.button>
                  );
                })
              )}
            </div>

            {/* Footer count */}
            {users && users.length > 0 && (
              <div className="border-t border-border/50 bg-muted/20 px-3 py-1.5">
                <p className="text-[10px] text-muted-foreground/60 font-medium tracking-wide uppercase">
                  {filteredUsers.length} / {users.length} users
                </p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function AdminActionPanel({
  config,
  onClose,
}: {
  config: AdminActionConfig;
  onClose: () => void;
}) {
  const { t } = useTranslation(["admin"]);
  // AdminActionPanel is only rendered for actions with mutationRef+fields (non-custom actions)
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const mutate = useMutation(config.mutationRef!);
  const fields = config.fields ?? [];

  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [executionState, setExecutionState] = useState<ExecutionState>("idle");
  const [result, setResult] = useState("");
  const [confirmValue, setConfirmValue] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);

  const handleSubmit = () => {
    if (config.dangerous && !showConfirm) {
      setShowConfirm(true);
      return;
    }

    if (config.dangerous && confirmValue !== config.id) {
      return;
    }

    void executeAction();
  };

  const executeAction = async () => {
    setExecutionState("loading");
    setResult("");
    setShowConfirm(false);
    setConfirmValue("");

    try {
      // Dynamic dispatch: fieldValues is Record<string, string> — Convex IDs
      // are strings at runtime, so this is safe. Type safety is traded for the
      // flexibility of a registry-driven admin panel.
      const res = await mutate(fieldValues);
      setResult(typeof res === "string" ? res : JSON.stringify(res));
      setExecutionState("success");
    } catch (err) {
      setResult(err instanceof Error ? err.message : String(err));
      setExecutionState("error");
    }
  };

  const canSubmit =
    executionState !== "loading" &&
    fields.every((f) => fieldValues[f.name]?.trim());

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/30 backdrop-blur-[2px] pt-[8vh] pb-12"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 10, scale: 0.98 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-lg mx-4"
      >
        {/* Card */}
        <div className="rounded-2xl border bg-card shadow-2xl shadow-black/10">
          {/* Danger stripe */}
          {config.dangerous && (
            <div className="h-1 rounded-t-2xl bg-gradient-to-r from-amber-400 via-orange-400 to-red-400" />
          )}

          {/* Header */}
          <div className="px-6 pt-5 pb-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <div
                  className={`
                    mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl
                    ${config.dangerous ? "bg-amber-500/10 ring-1 ring-amber-500/20" : "bg-muted ring-1 ring-border"}
                  `}
                >
                  {config.dangerous ? (
                    <ShieldAlert className="h-5 w-5 text-amber-600" />
                  ) : (
                    <CheckCircle2 className="h-5 w-5 text-muted-foreground" />
                  )}
                </div>
                <div>
                  <h2
                    className="text-lg font-bold text-foreground"
                    style={{ fontFamily: "'JetBrains Mono', monospace" }}
                  >
                    {config.label}
                  </h2>
                  <p className="mt-0.5 text-xs text-muted-foreground leading-relaxed">
                    {config.description}
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={onClose}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground/60 transition-colors hover:bg-muted hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Danger banner */}
          {config.dangerous && (
            <div className="mx-6 mb-4 flex items-center gap-2.5 rounded-lg bg-amber-50 border border-amber-200/60 px-3.5 py-2.5 text-xs text-amber-800">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-500" />
              <span className="font-medium">{t("actions.dangerWarning")}</span>
            </div>
          )}

          {/* Form fields */}
          <div className="px-6 space-y-4">
            {fields.map((field) => (
              <div key={field.name} className="space-y-1.5">
                <label
                  htmlFor={field.name}
                  className="text-xs font-semibold uppercase tracking-wider text-muted-foreground"
                >
                  {field.label}
                </label>
                {field.type === "user-select" ? (
                  <UserSelectField
                    fieldName={field.name}
                    placeholder={field.placeholder}
                    value={fieldValues[field.name] ?? ""}
                    onChange={(val) =>
                      setFieldValues((prev) => ({
                        ...prev,
                        [field.name]: val,
                      }))
                    }
                  />
                ) : (
                  <Input
                    id={field.name}
                    placeholder={field.placeholder}
                    value={fieldValues[field.name] ?? ""}
                    onChange={(e) =>
                      setFieldValues((prev) => ({
                        ...prev,
                        [field.name]: e.target.value,
                      }))
                    }
                    className="h-11"
                  />
                )}
              </div>
            ))}
          </div>

          {/* Confirmation */}
          {showConfirm && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              transition={{ duration: 0.2 }}
              className="mx-6 mt-4"
            >
              <div className="rounded-lg border-2 border-dashed border-red-300/60 bg-red-50/50 p-4 space-y-2.5">
                <p className="text-xs font-bold text-red-700 uppercase tracking-wider">
                  {t("actions.confirmTitle")}
                </p>
                <p className="text-xs text-red-600/80">
                  {t("actions.confirmDescription", { actionId: config.id })}
                </p>
                <Input
                  placeholder={t("actions.confirmPlaceholder")}
                  value={confirmValue}
                  onChange={(e) => setConfirmValue(e.target.value)}
                  autoFocus
                  className="h-10 border-red-200 bg-white focus:ring-red-400/30 font-mono text-sm"
                />
              </div>
            </motion.div>
          )}

          {/* Action bar */}
          <div className="px-6 pt-5 pb-5 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={onClose}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              {t("actions.cancel")}
            </button>

            <Button
              onClick={handleSubmit}
              disabled={
                !canSubmit || (showConfirm && confirmValue !== config.id)
              }
              variant={config.dangerous ? "danger" : "primary"}
              className="min-w-[140px] h-10"
            >
              {executionState === "loading" ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  {t("actions.executing")}
                </span>
              ) : (
                t("actions.submit")
              )}
            </Button>
          </div>

          {/* Result banner */}
          {executionState === "success" && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="border-t border-green-200 bg-green-50 px-6 py-4 rounded-b-2xl"
            >
              <div className="flex items-start gap-2.5">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
                <div>
                  <p className="text-sm font-semibold text-green-800">
                    {t("actions.success")}
                  </p>
                  {result && (
                    <p className="mt-1 text-xs text-green-700/70 font-mono break-all">
                      {result}
                    </p>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {executionState === "error" && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="border-t border-red-200 bg-red-50 px-6 py-4 rounded-b-2xl"
            >
              <div className="flex items-start gap-2.5">
                <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
                <div>
                  <p className="text-sm font-semibold text-red-800">
                    {t("actions.error")}
                  </p>
                  {result && (
                    <p className="mt-1 text-xs text-red-700/70 font-mono break-all">
                      {result}
                    </p>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
